import dayjs from 'dayjs'
import { FC, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'

import { Permission, SubscriptionStatus } from 'common/types/enums'
import { CustomerStatuses } from 'common/types/utils'
import TableFilter from 'components/tables/TableFilter'
import { useGetGroupsQuery } from 'common/services/useGroup'
import { fromParam, toParam } from 'common/utils/urls/param'
import { usePermission, useUser } from 'common/services/useUser'
import type { FilterInputType } from 'components/tables/FilterInput'
import { useGetTeamMembersQuery } from 'common/services/useTeamMember'
import DateRangePicker from 'components/base/forms/date/DateRangePicker'

type GroupWithId = { id: number; name?: string; users?: { user_id: number }[] }

type SubscriptionTableFiltersProps = {
  onFilterChange: (filters: {
    searchInput: string
    dateFrom?: string
    dateTo?: string
    status: SubscriptionStatus[]
    groups: number[]
    members: number[]
    page: number
  }) => void
}

const SubscriptionTableFilters: FC<SubscriptionTableFiltersProps> = ({
  onFilterChange,
}) => {
  const router = useRouter()
  const params = fromParam()
  const {
    date_from,
    date_to,
    groups: groupsParams,
    members: membersParams,
    q,
    status: statusParams,
  } = params

  const isGroupUser = usePermission(Permission.GROUP_ONLY)
  const { data: user } = useUser({})
  const { data: allGroups } = useGetGroupsQuery({})
  const { data: allMembers } = useGetTeamMembersQuery({})

  const [dateFrom, setDateFrom] = useState(date_from)
  const [dateTo, setDateTo] = useState(date_to)
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState(q || '')
  const [status, setStatus] = useState<SubscriptionStatus[]>(
    statusParams?.split(',') || [],
  )
  const [groups, setGroups] = useState<number[]>(groupsParams?.split(',') || [])
  const [members, setMembers] = useState<number[]>(
    membersParams?.split(',') || [],
  )

  const availableGroups = isGroupUser
    ? user?.groups.map((group) => ({ ...group, id: group.group_id }))
    : allGroups?.results

  useEffect(() => {
    router.replace(
      `${document.location.pathname}?${toParam({
        date_from: dateFrom,
        date_to: dateTo,
        page,
        q: searchInput,
      })}`,
    )

    onFilterChange({
      dateFrom,
      dateTo,
      groups,
      members,
      page,
      searchInput,
      status,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, dateFrom, dateTo, page, members, groups, status])

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }
  const filters = useMemo(() => {
    const memberList = allMembers?.results ?? []
    const membersInGroups = memberList.filter(
      (member) =>
        availableGroups?.some(
          (g) => 'users' in g && g.users?.some((u) => u.user_id === member.id),
        ),
    )
    const getMember = (id: number) => memberList.find((m) => m.id === id)

    const noGroupsMessage = (
      <div className='d-flex text-center text-muted flex-column gap-0'>
        You have no groups.
        <br />
        Create them in the Team page.
      </div>
    )
    const noMembersMessage = (
      <div className='d-flex text-center text-muted flex-column gap-0'>
        You have no groups that contain members.
        <br />
        Create them in the Team page.
      </div>
    )

    const membersFilter = isGroupUser
      ? null
      : {
          getLabel: (option: number) => {
            const m = getMember(option)
            return `${m?.first_name ?? ''} ${m?.last_name ?? ''}`.trim()
          },
          getSearch: (option: number) => {
            const m = getMember(option)
            return `${m?.first_name ?? ''} ${m?.last_name ?? ''} ${
              m?.email ?? ''
            }`.trim()
          },
          label: 'Members',
          onChange: membersInGroups.length ? setMembers : () => {},
          options: membersInGroups.length
            ? membersInGroups.map((m) => m.id)
            : [null],
          renderOption: (option: number) => {
            if (!membersInGroups.length) return noMembersMessage
            const m = getMember(option)
            return (
              <div className='d-flex mb-2 flex-column gap-0'>
                <div className='fw-semibold'>
                  {m?.first_name} {m?.last_name}
                </div>
                <div className='fs-sm text-muted lh-1'>{m?.email}</div>
              </div>
            )
          },
          value: members,
        }

    const groupsFilter = {
      label: 'Groups',
      onChange: setGroups,
      options: isGroupUser ? [] : availableGroups?.map((g) => g.id) ?? [],
      renderOption: (option: number | string) => {
        if (!availableGroups?.length) return noGroupsMessage
        return (availableGroups as GroupWithId[]).find((g) => g.id === option)
          ?.name
      },
      value: groups,
    }

    const statusFilter = {
      label: 'Status',
      onChange: setStatus,
      options: CustomerStatuses as any,
      value: status,
    }

    return [
      ...(membersFilter != null ? [membersFilter] : []),
      groupsFilter,
      statusFilter,
    ] as FilterInputType['filters']
  }, [
    isGroupUser,
    availableGroups,
    allMembers?.results,
    status,
    groups,
    members,
  ])

  return (
    <div className='d-flex align-items-center justify-content-between gap-4'>
      <div className='flex-1'>
        <TableFilter
          placeholder='Search subscriptions...'
          setPage={handlePageChange}
          customFilters={
            <div className='me-4'>
              <DateRangePicker
                selectRange
                showDoubleView
                placeholder='Filter by date'
                showNeighboringMonth={false}
                onChange={(v) => {
                  if (v?.[0] && v?.[1]) {
                    setDateFrom(dayjs(v[0]).format('YYYY-MM-DD'))
                    setDateTo(dayjs(v[1]).format('YYYY-MM-DD'))
                  } else {
                    setDateFrom(undefined)
                    setDateTo(undefined)
                  }
                }}
                value={
                  dateFrom && dateTo
                    ? [
                        dayjs(dateFrom, 'YYYY-MM-DD').toISOString(),
                        dayjs(dateTo, 'YYYY-MM-DD').toISOString(),
                      ]
                    : null
                }
              />
            </div>
          }
          filters={filters}
          onChange={setSearchInput}
          value={searchInput}
        />
      </div>
    </div>
  )
}

export default SubscriptionTableFilters
